import { useState, useEffect } from 'react';
import {
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Button,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import { TextField, MenuItem } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import fr from 'date-fns/locale/fr';
import api from '../../../services/api';
import './styles.css';

interface Product {
    _id: string;
    name: string;
}

interface Slot {
    _id: string;
    date: string;
    productId: string | { toString(): string };
    time: string;
    capacity: number;
    isAvailable: boolean;
    createdAt: string;
    remainingCapacity?: number;
    totalCapacity?: number;
    reserved?: number;
}

const AdminOpenSlots = () => {
    const [slots, setSlots] = useState<Slot[]>([]);
    const [filteredSlots, setFilteredSlots] = useState<Slot[]>([]);
    const [products, setProducts] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [slotToDelete, setSlotToDelete] = useState<Slot | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedProductId, setSelectedProductId] = useState<string>('');

    // États pour l'édition
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [slotToEdit, setSlotToEdit] = useState<Slot | null>(null);
    const [editForm, setEditForm] = useState({ time: '', capacity: 6, date: '' });

    const AVAILABLE_TIMES = ['08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '17:30'];

    const slotProductId = (slot: Slot) => String(slot.productId);

    const applyFilters = (slotsToFilter: Slot[], date: Date | null, productId: string) => {
        let next = slotsToFilter;

        if (date) {
            const year = date.getFullYear();
            const month = date.getMonth();
            const day = date.getDate();
            next = next.filter((slot) => {
                const slotDate = new Date(slot.date);
                return (
                    slotDate.getFullYear() === year &&
                    slotDate.getMonth() === month &&
                    slotDate.getDate() === day
                );
            });
        }

        if (productId) {
            next = next.filter((slot) => slotProductId(slot) === productId);
        }

        setFilteredSlots(next);
    };

    const fetchProducts = async () => {
        try {
            const response = await api.get('/products');
            const data = response.data;
            const productsMap = data.reduce((acc: Record<string, string>, product: Product) => {
                acc[product._id] = product.name;
                return acc;
            }, {});
            setProducts(productsMap);
        } catch (error) {
            console.error('Erreur lors de la récupération des produits:', error);
        }
    };

    const fetchSlots = async () => {
        try {
            setLoading(true);
            const response = await api.get('/planning/slots/all');
            const data = response.data;
            setSlots(data);
            applyFilters(data, selectedDate, selectedProductId);
        } catch (error) {
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (slot: Slot) => {
        setSlotToDelete(slot);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!slotToDelete) return;

        try {
            await api.delete(`/planning/slots/${slotToDelete._id}`);

            // Mettre à jour la liste des créneaux
            const updatedSlots = slots.filter(slot => slot._id !== slotToDelete._id);
            setSlots(updatedSlots);

            applyFilters(updatedSlots, selectedDate, selectedProductId);

            setDeleteDialogOpen(false);
            setSlotToDelete(null);
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
        }
    };

    const handleEditClick = (slot: Slot) => {
        setSlotToEdit(slot);
        // Extraire la date au format YYYY-MM-DD depuis la date du slot
        const slotDate = new Date(slot.date);
        const dateStr = `${slotDate.getFullYear()}-${String(slotDate.getMonth() + 1).padStart(2, '0')}-${String(slotDate.getDate()).padStart(2, '0')}`;
        setEditForm({ time: slot.time, capacity: slot.capacity, date: dateStr });
        setEditDialogOpen(true);
    };

    const handleEditConfirm = async () => {
        if (!slotToEdit) return;

        try {
            await api.put(`/planning/slots/${slotToEdit._id}`, {
                time: editForm.time,
                capacity: editForm.capacity,
                date: editForm.date
            });

            // Rafraîchir la liste
            await fetchSlots();
            setEditDialogOpen(false);
            setSlotToEdit(null);
        } catch (error) {
            console.error('Erreur lors de la modification:', error);
        }
    };

    const handleDateChange = (date: Date | null) => {
        setSelectedDate(date);
        applyFilters(slots, date, selectedProductId);
    };

    const handleProductChange = (productId: string) => {
        setSelectedProductId(productId);
        applyFilters(slots, selectedDate, productId);
    };

    const clearDateFilter = () => {
        setSelectedDate(null);
        applyFilters(slots, null, selectedProductId);
    };

    const clearProductFilter = () => {
        setSelectedProductId('');
        applyFilters(slots, selectedDate, '');
    };

    const clearAllFilters = () => {
        setSelectedDate(null);
        setSelectedProductId('');
        setFilteredSlots(slots);
    };

    useEffect(() => {
        fetchProducts();
        fetchSlots();
    }, []);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const hasActiveFilters = Boolean(selectedDate) || Boolean(selectedProductId);
    const productOptions = Object.entries(products).sort((a, b) =>
        a[1].localeCompare(b[1], 'fr', { sensitivity: 'base' })
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
            <Box className="admin-open-slots">
                <Paper className="slots-paper">
                    <Box className="header-section">
                        <Typography variant="h4" className="title">
                            Créneaux ouverts
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={() => {
                                fetchProducts();
                                fetchSlots();
                            }}
                            disabled={loading}
                        >
                            Rafraîchir
                        </Button>
                    </Box>

                    <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                        <DatePicker
                            label="Filtrer par date"
                            value={selectedDate}
                            onChange={handleDateChange}
                            slotProps={{
                                textField: {
                                    fullWidth: false,
                                    variant: 'outlined',
                                    size: 'small',
                                    sx: { width: 200 }
                                }
                            }}
                        />
                        {selectedDate && (
                            <Tooltip title="Effacer le filtre date">
                                <IconButton onClick={clearDateFilter} size="small">
                                    <ClearIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        <TextField
                            select
                            label="Filtrer par produit"
                            value={selectedProductId}
                            onChange={(e) => handleProductChange(e.target.value)}
                            size="small"
                            sx={{ minWidth: 220 }}
                        >
                            <MenuItem value="">Tous les produits</MenuItem>
                            {productOptions.map(([id, name]) => (
                                <MenuItem key={id} value={id}>
                                    {name}
                                </MenuItem>
                            ))}
                        </TextField>
                        {selectedProductId && (
                            <Tooltip title="Effacer le filtre produit">
                                <IconButton onClick={clearProductFilter} size="small">
                                    <ClearIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ flexBasis: '100%', mt: -0.5 }}>
                            {selectedDate && selectedProductId
                                ? `Créneaux du ${formatDate(selectedDate.toISOString())} — ${products[selectedProductId] || 'Produit'}`
                                : selectedDate
                                    ? `Créneaux du ${formatDate(selectedDate.toISOString())}`
                                    : selectedProductId
                                        ? `Produit : ${products[selectedProductId] || selectedProductId}`
                                        : 'Tous les créneaux'}
                        </Typography>
                        {hasActiveFilters && filteredSlots.length === 0 && (
                            <Chip
                                label="Aucun créneau pour ces critères"
                                color="warning"
                                size="small"
                            />
                        )}
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Heure</TableCell>
                                    <TableCell>Produit</TableCell>
                                    <TableCell>Capacité</TableCell>
                                    <TableCell>Statut</TableCell>
                                    <TableCell>Créé le</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredSlots.length === 0 && !hasActiveFilters ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            Aucun créneau disponible
                                        </TableCell>
                                    </TableRow>
                                ) : filteredSlots.length > 0 ? (
                                    filteredSlots.map((slot) => (
                                        <TableRow key={slot._id}>
                                            <TableCell>{formatDate(slot.date)}</TableCell>
                                            <TableCell>{slot.time}</TableCell>
                                            <TableCell>
                                                {products[slotProductId(slot)] || slotProductId(slot)}
                                            </TableCell>
                                            <TableCell>
                                                {slot.remainingCapacity !== undefined && slot.totalCapacity !== undefined
                                                    ? `${slot.remainingCapacity}/${slot.totalCapacity}`
                                                    : slot.capacity
                                                }
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={slot.isAvailable ? 'Disponible' : 'Indisponible'}
                                                    color={slot.isAvailable ? 'success' : 'error'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {new Date(slot.createdAt).toLocaleString('fr-FR')}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleEditClick(slot)}
                                                    title="Modifier le créneau"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => handleDeleteClick(slot)}
                                                    title="Supprimer le créneau"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Box sx={{ p: 2 }}>
                                                <Typography variant="subtitle1">
                                                    Aucun créneau ne correspond aux filtres sélectionnés.
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    onClick={clearAllFilters}
                                                    sx={{ mt: 2 }}
                                                >
                                                    Réinitialiser les filtres
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                >
                    <DialogTitle>Confirmer la suppression</DialogTitle>
                    <DialogContent>
                        Êtes-vous sûr de vouloir supprimer ce créneau ?
                        <br />
                        Date: {slotToDelete && formatDate(slotToDelete.date)}
                        <br />
                        Heure: {slotToDelete?.time}
                        <br />
                        Produit:{' '}
                        {slotToDelete &&
                            (products[slotProductId(slotToDelete)] || slotProductId(slotToDelete))}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
                        <Button onClick={handleDeleteConfirm} color="error">
                            Supprimer
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog de modification */}
                <Dialog
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                >
                    <DialogTitle>Modifier le créneau</DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 300 }}>
                            <DatePicker
                                label="Date"
                                value={editForm.date ? new Date(editForm.date + 'T00:00:00') : null}
                                onChange={(newDate: Date | null) => {
                                    if (newDate) {
                                        const dateStr = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}`;
                                        setEditForm({ ...editForm, date: dateStr });
                                    }
                                }}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        variant: 'outlined'
                                    }
                                }}
                            />
                            <Typography variant="body2" color="text.secondary">
                                Produit:{' '}
                                {slotToEdit &&
                                    (products[slotProductId(slotToEdit)] || slotProductId(slotToEdit))}
                            </Typography>
                            <TextField
                                select
                                label="Heure"
                                value={editForm.time}
                                onChange={(e) => setEditForm({ ...editForm, time: e.target.value })}
                                fullWidth
                            >
                                {AVAILABLE_TIMES.map((time) => (
                                    <MenuItem key={time} value={time}>
                                        {time}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                type="number"
                                label="Capacité"
                                value={editForm.capacity}
                                onChange={(e) => setEditForm({ ...editForm, capacity: parseInt(e.target.value) })}
                                fullWidth
                                inputProps={{ min: 1 }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditDialogOpen(false)}>Annuler</Button>
                        <Button onClick={handleEditConfirm} variant="contained" color="primary">
                            Enregistrer
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
};

export default AdminOpenSlots;